/**
 * As contas da página de Perícias, fora do componente para poderem ser testadas.
 *
 * <p>Nada aqui busca dado: a página recebe a consulta do backend (`PericiaService.java`)
 * e o documento da Tier List, e estas funções só dizem como ler os dois.
 */
import type { Item, ItemDaPericia } from '../mundo/tipos';
import { COLETAS, COMBATE_DEFENSIVAS, COMBATE_OFENSIVAS, CRIACOES } from './pericias';
import type { Pericia } from './pericias';

/** Os grupos da lista lateral, na ordem da Ficha. */
export const GRUPOS = [
  { rotulo: 'Coleta', pericias: COLETAS },
  { rotulo: 'Criação', pericias: CRIACOES },
  { rotulo: 'Combate · ofensiva', pericias: COMBATE_OFENSIVAS },
  { rotulo: 'Combate · defensiva', pericias: COMBATE_DEFENSIVAS },
];

export type Natureza = 'coleta' | 'criacao' | 'combate';

export function naturezaDe(id: string): Natureza {
  if (COLETAS.some((p) => p.id === id)) return 'coleta';
  if (CRIACOES.some((p) => p.id === id)) return 'criacao';
  return 'combate';
}

/**
 * O par de uma coleta é a criação que sai dela, e vice-versa: Mineração alimenta Forja.
 *
 * <p>Lê o pareamento por posição, que `pericias.ts` garante nas três primeiras. Tecnomagia
 * não tem coleta própria, e por isso não tem par — é o caso que quebra a simetria, e
 * está escrito lá.
 */
export function parDe(id: string): Pericia | undefined {
  const i = COLETAS.findIndex((p) => p.id === id);
  if (i >= 0) return CRIACOES[i];
  const j = CRIACOES.findIndex((p) => p.id === id);
  return j >= 0 ? COLETAS[j] : undefined;
}

export type TipoDeFonte = 'coleta' | 'criatura' | 'npc' | 'quest';

export interface Fonte {
  tipo: TipoDeFonte;
  rotulo: string;
  nomes: string[];
}

/**
 * O nome de um local para quem lê: o local padrão de uma área se chama como ela, e a nota
 * já diz "Campos de trigo (área toda)" — repetir o nome antes só duplicaria.
 */
export function ondeFica(local: { nome: string; nota?: string }): string {
  if (!local.nota) return local.nome;
  return local.nota.startsWith(local.nome) ? local.nota : `${local.nome} (${local.nota})`;
}

/**
 * Como se consegue um item: achando-o num local, abatendo a criatura que o dropa,
 * comprando do NPC que o vende, ou como recompensa de quest. Só entra o que tem nome —
 * uma fonte vazia não diz nada a quem lê.
 */
export function fontesDoItem(item: Item): Fonte[] {
  const fontes: Fonte[] = [
    {
      tipo: 'coleta',
      rotulo: 'Coletando em',
      nomes: item.locais.map(ondeFica),
    },
    { tipo: 'criatura', rotulo: 'Abatendo', nomes: item.dropadoPor.map((c) => c.nome) },
    { tipo: 'npc', rotulo: 'Comprando de', nomes: item.vendidoPor.map((n) => n.nome) },
    { tipo: 'quest', rotulo: 'Recompensa de', nomes: item.recompensaDe.map((q) => q.nome) },
  ];
  return fontes.filter((f) => f.nomes.length > 0);
}

/** `"T3"` vira 3; o que não é tier vira `undefined`, e vai para o fim da lista. */
export const numeroDoTier = (tier?: string) => {
  const n = tier ? Number(tier.replace(/^T/i, '')) : NaN;
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : undefined;
};

/** Do tier mais baixo ao mais alto; sem tier no fim, por nome. */
export function porTier(a: ItemDaPericia, b: ItemDaPericia): number {
  const ta = numeroDoTier(a.item.tier) ?? 99;
  const tb = numeroDoTier(b.item.tier) ?? 99;
  return ta - tb || a.item.nome.localeCompare(b.item.nome);
}

/** A lore de uma perícia: três textos com nome, gravados no documento da página. */
export interface LoreDaPericia {
  origem: string;
  mestres: string;
  caminho: string;
}

export const BLOCOS_DE_LORE: { chave: keyof LoreDaPericia; titulo: string; dica: string }[] = [
  { chave: 'origem', titulo: 'Origem', dica: 'De onde o ofício vem no mundo: quem o descobriu, que povo o guarda.' },
  { chave: 'mestres', titulo: 'Mestres e escolas', dica: 'As tradições, guildas ou linhagens que ensinam a perícia.' },
  { chave: 'caminho', titulo: 'O caminho na Season 1', dica: 'Como o jogador entra na perícia e até onde ela o leva nesta Season.' },
];

/** O documento da página `pericias`: a lore, por id de perícia. */
export interface DocumentoDePericias {
  lore: Record<string, LoreDaPericia>;
}

export const loreEmBranco = (): LoreDaPericia => ({ origem: '', mestres: '', caminho: '' });

/** Lê o documento gravado; o que não for texto vira vazio, em vez de quebrar a tela. */
export function interpretarDocumento(guardado: unknown): DocumentoDePericias {
  const doc = (guardado ?? {}) as { lore?: Record<string, Partial<LoreDaPericia>> };
  const lore: Record<string, LoreDaPericia> = {};
  for (const [id, texto] of Object.entries(doc.lore ?? {})) {
    if (!texto || typeof texto !== 'object') continue;
    lore[id] = {
      origem: typeof texto.origem === 'string' ? texto.origem : '',
      mestres: typeof texto.mestres === 'string' ? texto.mestres : '',
      caminho: typeof texto.caminho === 'string' ? texto.caminho : '',
    };
  }
  return { lore };
}
